/**
 * Pitch-preserving WSOLA for recorded drum loops. Short waveform grains run at
 * their recorded sample rate; moving their centres changes duration only.
 * Strong attacks divide the recording into regions, so a kick/snare is never
 * repeated across a grain search or pulled away from its musical position.
 * Stereo channels share every splice decision to keep the image stable.
 */
export interface DrumStretchInput {
  channels: readonly Float32Array[];
  sampleRate: number;
  /** Musical period, excluding codec padding. Missing end samples are silence. */
  sourceFrames: number;
  /** Target tempo / recorded tempo. */
  rate: number;
}

const YIELD_AFTER_MS = 8;

/** Cooperative by default: even long loops leave room for input and Stop. */
export async function stretchDrumLoop(
  input: DrumStretchInput,
  yieldWork: () => Promise<void> = () => new Promise((resolve) => setTimeout(resolve, 0)),
): Promise<Float32Array[]> {
  const { channels, sampleRate, rate } = input;
  if (
    !channels.length ||
    !Number.isFinite(sampleRate) ||
    sampleRate <= 0 ||
    !Number.isFinite(rate) ||
    rate <= 0 ||
    !Number.isFinite(input.sourceFrames) ||
    input.sourceFrames < 1
  ) {
    throw new RangeError(
      'A drum stretch needs audio, a sample rate, a period and a positive tempo ratio.',
    );
  }
  const sourceFrames = Math.round(input.sourceFrames);
  const targetFrames = Math.max(1, Math.round(sourceFrames / rate));
  const output = channels.map(() => new Float32Array(targetFrames));
  const read = (channel: number, index: number) => channels[channel][index] ?? 0;
  if (Math.abs(rate - 1) < 1e-8) {
    output.forEach((channel, index) => channel.set(channels[index].subarray(0, targetFrames)));
    return output;
  }
  let lastYield = performance.now();
  const maybeYield = async () => {
    if (performance.now() - lastYield < YIELD_AFTER_MS) return;
    await yieldWork();
    lastYield = performance.now();
  };

  // A 2ms peak envelope retains short stick attacks, including opposite-phase
  // stereo hits which would disappear in a simple L+R analysis mix.
  const block = Math.max(1, Math.round(sampleRate * 0.002));
  const envelope = new Float32Array(Math.ceil(sourceFrames / block));
  let peak = 0;
  for (let start = 0; start < sourceFrames; start += block) {
    let amplitude = 0;
    for (let i = start; i < Math.min(sourceFrames, start + block); i++) {
      for (let c = 0; c < channels.length; c++)
        amplitude = Math.max(amplitude, Math.abs(read(c, i)));
    }
    envelope[Math.floor(start / block)] = amplitude;
    peak = Math.max(peak, amplitude);
    if (start % (block * 128) === 0) await maybeYield();
  }
  const boundaries = [0];
  const minimumSpacing = Math.round(sampleRate * 0.045);
  for (let b = 1; b < envelope.length; b++) {
    let previous = 0;
    for (let p = Math.max(0, b - 8); p < b; p++) previous = Math.max(previous, envelope[p]);
    if (envelope[b] <= peak * 0.08 || envelope[b] <= previous * 2.2) continue;
    // Locate the first rise inside the envelope block, rather than quantizing
    // the hit to our analysis grid (a 2ms flam is audible on a rimshot).
    const threshold = Math.max(peak * 0.015, previous * 1.5);
    let onset = b * block;
    while (
      onset < Math.min(sourceFrames, (b + 1) * block) &&
      channels.every((_channel, c) => Math.abs(read(c, onset)) < threshold)
    )
      onset++;
    if (
      onset - boundaries[boundaries.length - 1] >= minimumSpacing &&
      sourceFrames - onset >= minimumSpacing
    )
      boundaries.push(onset);
  }
  boundaries.push(sourceFrames);

  const halfWindow = Math.max(8, Math.round(sampleRate * 0.02));
  const hop = Math.max(4, Math.round(sampleRate * 0.01));
  const searchRadius = Math.max(2, Math.round(sampleRate * 0.005));
  const sampleStep = Math.max(1, Math.floor(sampleRate / 11025));
  const candidateStep = Math.max(1, Math.floor(sampleRate / 11025));
  const weights = new Float32Array(targetFrames);
  const window = new Float32Array(2 * halfWindow);
  for (let i = 0; i < window.length; i++)
    window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / window.length);

  for (let region = 0; region < boundaries.length - 1; region++) {
    const sourceStart = boundaries[region];
    const sourceEnd = boundaries[region + 1];
    const targetStart = Math.round(sourceStart / rate);
    const targetEnd = Math.min(targetFrames, Math.round(sourceEnd / rate));
    const centres: number[] = [];
    for (let centre = targetStart; centre < targetEnd; centre += hop) centres.push(centre);
    centres.push(targetEnd);
    for (const centre of centres) {
      const local = centre - targetStart;
      const remaining = targetEnd - centre;
      let sourceCentre = sourceStart + Math.round(local * rate);
      if (local < halfWindow) {
        // The first 20ms of an attack runs once at original speed.
        sourceCentre = sourceStart + local;
      } else if (remaining < halfWindow) {
        sourceCentre = sourceEnd - remaining;
      } else {
        // Do not let a slow-tempo search reach back over an attack already
        // copied by the pinned opening grains (that produces a quiet flam).
        sourceCentre = Math.max(
          sourceStart + halfWindow,
          Math.min(sourceEnd - halfWindow, sourceCentre),
        );
        const lower = Math.max(sourceStart + halfWindow, sourceCentre - searchRadius);
        const upper = Math.min(sourceEnd - halfWindow, sourceCentre + searchRadius);
        let best = -Infinity;
        let bestCentre = sourceCentre;
        for (let candidate = lower; candidate <= upper; candidate += candidateStep) {
          let cross = 0;
          let energy = 0;
          for (let delta = -halfWindow; delta < 0; delta += sampleStep) {
            const target = centre + delta;
            if (target < targetStart || weights[target] < 1e-6) continue;
            for (let c = 0; c < channels.length; c++) {
              const sample = read(c, candidate + delta);
              cross += (sample * output[c][target]) / weights[target];
              energy += sample * sample;
            }
          }
          // Normalized correlation prevents the search preferring the loudest
          // grain; the tiny distance penalty resolves silent/periodic ties.
          const score =
            cross / Math.sqrt(energy + 1e-12) - Math.abs(candidate - sourceCentre) * 1e-9;
          if (score > best) {
            best = score;
            bestCentre = candidate;
          }
        }
        sourceCentre = bestCentre;
      }
      for (let delta = -halfWindow; delta < halfWindow; delta++) {
        const target = centre + delta;
        const source = sourceCentre + delta;
        if (
          target < targetStart ||
          target >= targetEnd ||
          source < sourceStart ||
          source >= sourceEnd
        )
          continue;
        const weight = window[delta + halfWindow];
        weights[target] += weight;
        for (let c = 0; c < channels.length; c++) output[c][target] += read(c, source) * weight;
      }
      await maybeYield();
    }
  }
  for (let i = 0; i < targetFrames; i++) {
    if (weights[i] > 1e-6) for (const channel of output) channel[i] /= weights[i];
    if (i % 65536 === 0) await maybeYield();
  }
  return output;
}
